/**
 * ISMS Flow Diagram — data, types, and storage helpers.
 *
 * Defines the visual workflow matching the ISMS implementation flow diagram:
 * 4 phases, 12 columns, ~62 steps.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FlowGuidance {
  intro: string;
  tips: string[];
  doneWhen: string;
}

export interface FlowStep {
  id: string;
  label: string;
  isMilestone?: boolean;
  milestoneColour?: 'primary' | 'danger';
  path?: string;
  actionLabel?: string;
  icon: string;
  guidance: FlowGuidance;
  dependsOn?: string[]; // step IDs that should be completed before this one
}

export interface FlowColumn {
  id: string;
  label: string;
  icon: string;
  phaseIndex: number; // 0–3
  steps: FlowStep[];
}

export interface FlowPhase {
  id: string;
  label: string;
  colCount: number;
}

// ─── Phase definitions ───────────────────────────────────────────────────────

export const FLOW_PHASES: FlowPhase[] = [
  { id: 'context',   label: 'Context of the Organisation & ISMS Scope', colCount: 6 },
  { id: 'threat',    label: 'Threat & Risk Analysis',                   colCount: 2 },
  { id: 'gap',       label: 'Gap Analysis & Remediation',               colCount: 3 },
  { id: 'exception', label: 'Exception Management',                     colCount: 2 },
];

// ─── Column & step definitions ───────────────────────────────────────────────

export const FLOW_COLUMNS: FlowColumn[] = [

  // ── People Register ─────────────────────────────────────────────────────
  {
    id: 'people', label: 'People Register', icon: 'ri-user-line', phaseIndex: 0,
    steps: [
      {
        id: 'people-isms-manager', label: 'Add ISMS Manager',
        path: '/people', actionLabel: 'Go to People', icon: 'ri-user-star-line',
        guidance: {
          intro: 'The ISMS Manager is the person directly responsible for implementing and maintaining your ISO 27001 programme. This is the first person to add — everything else flows from this role.',
          tips: [
            'Add with their full legal name, job title, and email address.',
            'They need enough authority to request information from all departments.',
            'Typically the Information Security Manager, IT Manager, or whoever is leading the ISO 27001 project.',
            'Set their department clearly to reflect security or compliance responsibility.',
          ],
          doneWhen: 'Your ISMS Manager is in the People register with full name, title, and email.',
        },
      },
      {
        id: 'people-exec', label: 'Add Executive Leaders',
        path: '/people', actionLabel: 'Go to People', icon: 'ri-user-2-line',
        guidance: {
          intro: 'ISO 27001 requires visible senior management commitment. Executives need to be in the system so they can be assigned as Accountable owners on controls and risks.',
          tips: [
            'Add your CEO/MD, CTO/IT Director, CFO, and any C-level members involved in security decisions.',
            'Minimum: whoever can approve the ISMS policy and sign the Statement of Applicability.',
            'Include board members who will be named in the ISMS scope document.',
            'Focus on those who will be accountable for security decisions — not the entire company.',
          ],
          doneWhen: 'At least one senior management sponsor (CEO or equivalent) is in the People register.',
        },
      },
      {
        id: 'people-complete', label: 'Initial People Register Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'This milestone marks when you have a working initial People Register. You do not need every employee — just the key ISMS stakeholders.',
          tips: [
            'Check you have: ISMS Manager, at least one executive sponsor, IT lead, and key department heads.',
            'Can you assign Responsible, Accountable, Consulted, and Informed to real people for at least 5 controls?',
            'Do not worry about contractors or temporary staff yet.',
          ],
          doneWhen: 'At least 5 named individuals covering ISMS management, executive sponsorship, and IT operations.',
        },
      },
      {
        id: 'people-maintain', label: 'Modify People as Required',
        path: '/people', actionLabel: 'Go to People', icon: 'ri-user-settings-line',
        guidance: {
          intro: 'People change. As your ISMS matures, update job titles, add new hires, remove leavers, and adjust roles as your organisation evolves.',
          tips: [
            'Set a quarterly reminder to review the People register for accuracy.',
            'When someone leaves, check they are not assigned to active controls or risks before removing them.',
            'Add new starters within their first week so they can be assigned responsibilities promptly.',
          ],
          doneWhen: 'A process is in place to keep the People register current. This is ongoing.',
        },
      },
    ],
  },

  // ── Departments ──────────────────────────────────────────────────────────
  {
    id: 'departments', label: 'Departments', icon: 'ri-building-4-line', phaseIndex: 0,
    steps: [
      {
        id: 'dept-create', label: 'Create Departments',
        path: '/departments', actionLabel: 'Go to Departments', icon: 'ri-building-4-line',
        guidance: {
          intro: 'Departments define your organisational structure within the ISMS. They are used to assign ownership of assets, risks, and controls to the correct business unit, and are required before you can assign people to departments.',
          tips: [
            'Create a department for every business unit that owns, operates, or accesses information assets.',
            'Use the names your organisation actually uses — not generic terms like "IT" if your business calls it "Technology & Infrastructure".',
            'Include a department head (responsible person) for each entry.',
          ],
          doneWhen: 'All business units that appear in your scope are created as departments.',
        },
      },
      {
        id: 'dept-assign-people', label: 'Assign People to Departments',
        path: '/people', actionLabel: 'Go to People', icon: 'ri-user-settings-line',
        guidance: {
          intro: 'Every person in your People Register should belong to a department. This drives ownership reporting and helps auditors verify that every part of the business has an accountable person.',
          tips: [
            'Open each person record and set their department.',
            'A person can belong to one primary department — use the "role" field to reflect cross-functional responsibilities.',
            'Contractors and temporary staff should still be assigned to the department they operate within.',
          ],
          doneWhen: 'Every active person in the People Register has a department assigned.',
        },
      },
      {
        id: 'dept-complete', label: 'Departments Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'Your organisational structure is now reflected in the ISMS. Departments will be used throughout risk and control assignment to ensure accountability is clear at the business-unit level.',
          tips: [
            'Review departments any time the business restructures.',
            'Ensure every department has an active, named head — not a job title.',
          ],
          doneWhen: 'All departments are created and every person is assigned to one.',
        },
      },
    ],
  },

  // ── Physical Sites ───────────────────────────────────────────────────────
  {
    id: 'sites', label: 'Physical Sites', icon: 'ri-building-line', phaseIndex: 0,
    steps: [
      {
        id: 'sites-offices', label: 'Add Offices',
        path: '/physical-sites', actionLabel: 'Go to Physical Sites', icon: 'ri-building-2-line',
        guidance: {
          intro: 'Every office location where staff work with information assets needs to be recorded. Physical security controls apply at each site and auditors will ask which locations are in scope.',
          tips: [
            'Add all offices where employees regularly work, including home offices if applicable to your scope.',
            'For each site include: full address, type of facility, approximate headcount, and the physical security responsible person.',
            'Include satellite offices and co-working spaces used by permanent staff.',
          ],
          doneWhen: 'All main office locations where company information is accessed or processed are recorded.',
        },
      },
      {
        id: 'sites-datacenters', label: 'Add Data Centers',
        path: '/physical-sites', actionLabel: 'Go to Physical Sites', icon: 'ri-server-line',
        guidance: {
          intro: 'Data centres — whether owned, co-located, or leased rack space — are critical ISMS scope items. Annex A physical controls apply directly to these locations.',
          tips: [
            'Record every data centre including co-location facilities or rented rack space in a third-party DC.',
            'For third-party DCs, note the provider name and your SLA/contract reference.',
            'If you use only cloud infrastructure with no physical DC, record "No on-premises data centre" and note your primary cloud providers instead.',
          ],
          doneWhen: 'All data centre facilities (owned or third-party) are recorded with location and ownership details.',
        },
      },
      {
        id: 'sites-warehouses', label: 'Add Warehouses',
        path: '/physical-sites', actionLabel: 'Go to Physical Sites', icon: 'ri-store-2-line',
        guidance: {
          intro: 'If your organisation uses warehouses or storage facilities, these may contain physical assets or documents within ISMS scope.',
          tips: [
            'Only include warehouses where information assets, IT equipment, or physical media are stored.',
            'If no warehouses are relevant, note "Not applicable" in your scope document and skip this step.',
            'Consider whether any warehouse management systems contain sensitive data about customers or suppliers.',
          ],
          doneWhen: 'Relevant warehouses or storage facilities are recorded, or noted as not applicable.',
        },
      },
      {
        id: 'sites-complete', label: 'Physical Locations Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'Your physical footprint is documented. This is the foundation for physical security controls and will be referenced in your ISMS scope statement.',
          tips: [
            'Cross-check your physical locations list with insurance and facilities management records.',
            'Every location should have a named responsible person for physical security.',
          ],
          doneWhen: 'All relevant physical locations are documented and each has a responsible person assigned.',
        },
      },
      {
        id: 'sites-refine', label: 'Refine Physical Sites upon Change',
        path: '/physical-sites', actionLabel: 'Go to Physical Sites', icon: 'ri-refresh-line',
        guidance: {
          intro: 'Office moves, new facilities, and site closures need to be reflected in your ISMS. This is an ongoing maintenance task.',
          tips: [
            'Review physical sites any time a lease is signed, a site closes, or a new facility opens.',
            'Include remote working locations if your policy has extended to permanent home working.',
          ],
          doneWhen: 'Physical sites are kept up to date within 30 days of any change.',
        },
      },
    ],
  },

  // ── Assets ───────────────────────────────────────────────────────────────
  {
    id: 'assets', label: 'Assets', icon: 'ri-computer-line', phaseIndex: 0,
    steps: [
      {
        id: 'assets-laptops', label: 'Add Laptop Endpoints',
        path: '/assets', actionLabel: 'Go to Assets', icon: 'ri-macbook-line',
        guidance: {
          intro: 'Every laptop used by employees to access company information is an in-scope asset. ISO 27001 Annex A requires a complete asset inventory.',
          tips: [
            'Categorise by type (e.g. "Windows 11 laptops — 45 units") unless your auditor requires individual device records.',
            'Note the OS, whether devices are encrypted, and who manages them.',
            'Include personal devices (BYOD) if they access company email or internal systems.',
          ],
          doneWhen: 'Laptop endpoints are categorised and recorded with count, OS type, encryption status, and owner.',
        },
      },
      {
        id: 'assets-onprem', label: 'Add On-Prem Production Hardware',
        path: '/assets', actionLabel: 'Go to Assets', icon: 'ri-server-2-line',
        guidance: {
          intro: 'On-premises production servers, storage arrays, and network appliances are high-value assets requiring security classification.',
          tips: [
            'Include: physical servers, NAS/SAN storage, backup appliances, and hardware running business-critical systems.',
            'Record the location (which data centre/server room), the owner, and whether it is in active production or decommissioned.',
            'Hardware running end-of-life operating systems should be flagged — this is a common audit finding.',
          ],
          doneWhen: 'All production on-premises hardware is recorded with location, owner, and status.',
        },
      },
      {
        id: 'assets-cloud', label: 'Add Cloud Production Infrastructure',
        path: '/assets', actionLabel: 'Go to Assets', icon: 'ri-cloud-line',
        guidance: {
          intro: 'Cloud infrastructure (AWS, Azure, GCP accounts, virtual machines, databases) are assets. The shared responsibility model means you own the security of what you put in the cloud.',
          tips: [
            'Record cloud accounts/subscriptions, not individual resources. E.g. "AWS Production Account — 12 EC2, 3 RDS".',
            'Note the provider, account name, responsible owner, and data classification of data stored there.',
            'Include SaaS platforms (Microsoft 365, Salesforce, etc.) as software assets.',
          ],
          doneWhen: 'Cloud accounts and key platforms are recorded with provider, owner, and data classification.',
        },
      },
      {
        id: 'assets-networks', label: 'Add Office Networks',
        path: '/assets', actionLabel: 'Go to Assets', icon: 'ri-router-line',
        guidance: {
          intro: 'Network infrastructure — switches, firewalls, Wi-Fi access points, VPN gateways — are in-scope assets. Network security controls apply directly to these.',
          tips: [
            'Record network segments by location (e.g. "London Office LAN", "VPN Gateway").',
            'Note whether separate guest Wi-Fi networks exist and whether segmentation exists between IT and OT.',
            'Include managed network services provided by an ISP or managed service provider.',
          ],
          doneWhen: 'Key network infrastructure is catalogued by location with owner and security status noted.',
        },
      },
      {
        id: 'assets-software', label: 'Add Critical Software',
        path: '/assets', actionLabel: 'Go to Assets', icon: 'ri-code-s-slash-line',
        guidance: {
          intro: 'Business-critical software — ERP, CRM, finance platforms, custom applications — are information assets. Software vulnerabilities are a major attack surface.',
          tips: [
            'Focus on software that processes, stores, or transmits sensitive data (customer, financial, or personal data).',
            'Record: software name, vendor, version, whether on-premises or cloud, and the business owner.',
            'Flag any software running an unsupported version — common audit finding.',
          ],
          doneWhen: 'Critical software systems are listed with name, vendor, hosting model, and business owner.',
        },
      },
      {
        id: 'assets-complete', label: 'Asset List Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'Your initial asset inventory is complete. Every risk, control, and threat will reference the assets it protects.',
          tips: [
            'Cross-reference against your insurance schedule — anything insured should be in the ISMS inventory.',
            'Assign a data classification (Public, Internal, Confidential, Restricted) to each asset category.',
          ],
          doneWhen: 'All asset categories are recorded with owner, location, and classification. At least 80% of critical assets are covered.',
        },
      },
      {
        id: 'assets-refine', label: 'Refine Assets List',
        path: '/assets', actionLabel: 'Go to Assets', icon: 'ri-refresh-line',
        guidance: {
          intro: 'Asset inventories drift over time. New purchases, cloud deployments, and software changes all create new assets that need tracking.',
          tips: [
            'Review the asset list quarterly, or after any significant technology change.',
            'Integrate with your procurement process — new IT purchases should trigger an asset register update.',
          ],
          doneWhen: 'Asset register is reviewed and updated at least quarterly.',
        },
      },
    ],
  },

  // ── Context PESTLES ──────────────────────────────────────────────────────
  {
    id: 'pestles', label: 'Context (PESTLES)', icon: 'ri-global-line', phaseIndex: 0,
    steps: [
      {
        id: 'pestle-political', label: 'Complete Political Entries',
        path: '/pestle', actionLabel: 'Go to PESTLES', icon: 'ri-government-line',
        guidance: {
          intro: 'ISO 27001 clause 4.1 requires you to understand external context. Political factors can directly affect your information security posture and regulatory obligations.',
          tips: [
            'Consider: government data regulations in your jurisdiction, political stability, surveillance requirements, and encryption export controls.',
            'For UK organisations: UK GDPR, post-Brexit data flows, NCSC frameworks.',
            'Each factor needs a title, description, threat or opportunity classification, and impact level.',
          ],
          doneWhen: 'At least 2–3 political factors relevant to your industry and jurisdiction are recorded.',
        },
      },
      {
        id: 'pestle-economic', label: 'Complete Economic Entries',
        path: '/pestle', actionLabel: 'Go to PESTLES', icon: 'ri-line-chart-line',
        guidance: {
          intro: 'Economic conditions affect your security investment capacity, threat landscape, and supply chain security.',
          tips: [
            'Consider: budget constraints affecting security tooling, economic pressures on third-party suppliers, and whether a recession increases fraud or insider threat risk.',
            'Outsourcing trends driven by economic pressure often change your third-party risk profile.',
          ],
          doneWhen: 'At least 2 economic factors relevant to your organisation\'s security posture are documented.',
        },
      },
      {
        id: 'pestle-social', label: 'Complete Social Entries',
        path: '/pestle', actionLabel: 'Go to PESTLES', icon: 'ri-team-line',
        guidance: {
          intro: 'Social factors include workforce trends, remote working, social engineering risks, and public awareness of data privacy — all of which shape your human security risks.',
          tips: [
            'Consider: remote/hybrid working (VPN, endpoint, and home network risks), social engineering susceptibility, and employee security awareness culture.',
            'Social media use by employees can expose information — include if relevant.',
            'Demographics of your customer base may affect data protection obligations.',
          ],
          doneWhen: 'Key social factors affecting your information security culture and human risks are recorded.',
        },
      },
      {
        id: 'pestle-legal', label: 'Complete Legal Entries',
        path: '/pestle', actionLabel: 'Go to PESTLES', icon: 'ri-scales-line',
        guidance: {
          intro: 'Legal and regulatory requirements form the basis of your compliance obligations. These drive your control selections and your Legal Register.',
          tips: [
            'Key legislation: GDPR/UK GDPR, sector-specific rules (PCI-DSS, FCA, NHS DSP Toolkit), Computer Misuse Act, employment law.',
            'For each legal factor: regulation name, who it applies to, key obligation, and risk of non-compliance.',
            'These PESTLES entries feed directly into your Legal Register.',
          ],
          doneWhen: 'All major applicable laws and regulations are listed as legal PESTLES factors.',
        },
      },
      {
        id: 'pestle-environmental', label: 'Complete Environmental Entries',
        path: '/pestle', actionLabel: 'Go to PESTLES', icon: 'ri-leaf-line',
        guidance: {
          intro: 'Physical environmental factors affect the availability and resilience of your information systems — from natural disasters to climate change impacts.',
          tips: [
            'Consider: flood risk at physical locations, power supply reliability, extreme weather, and climate-related supply chain disruption.',
            'If your data centre is in a flood zone, this is a direct availability risk affecting your BCP/DR plans.',
            'Sustainability requirements from customers or regulators may drive new security processes.',
          ],
          doneWhen: 'Physical environmental risks relevant to your infrastructure locations are documented.',
        },
      },
      {
        id: 'pestle-security', label: 'Add Security Entries',
        path: '/pestle', actionLabel: 'Go to PESTLES', icon: 'ri-shield-line',
        guidance: {
          intro: 'The Security dimension captures specific cyber and physical security threats in your operating environment — the threat landscape your organisation actually faces.',
          tips: [
            'Consider: sector-specific cyber threats (ransomware targeting your industry), nation-state threats, physical security risks, supply chain compromise.',
            'Reference the NCSC Threat Report and sector-specific threat intelligence.',
            'Security entries here directly feed the Threat Register in the next phase.',
          ],
          doneWhen: 'The main security threats in your external context are documented with impact and type classification.',
        },
      },
      {
        id: 'pestle-complete', label: 'PESTLE Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'A complete PESTLES assessment satisfies ISO 27001 clause 4.1 (external context) and informs your risk assessment and control selection.',
          tips: [
            'Each category (P, E, S, T, L, E, S) should have at least 2 entries — ideally 3–5.',
            'Have the assessment reviewed by people from each area (legal for Legal entries, finance for Economic, etc.).',
          ],
          doneWhen: 'All seven PESTLES categories have at least 2 documented factors each.',
        },
      },
      {
        id: 'pestle-refine', label: 'Refine PESTLES when needed',
        path: '/pestle', actionLabel: 'Go to PESTLES', icon: 'ri-refresh-line',
        guidance: {
          intro: 'The external context changes. New regulations, emerging cyber threats, and geopolitical events all affect your PESTLES. Review annually at minimum.',
          tips: [
            'Add a PESTLES review to your annual ISMS management review agenda.',
            'Any significant external change (new regulation, major incident, market change) should trigger an ad-hoc review.',
          ],
          doneWhen: 'PESTLES is reviewed at least annually and updated after significant external changes.',
        },
      },
    ],
  },

  // ── Legal Register ───────────────────────────────────────────────────────
  {
    id: 'legal', label: 'Legal Register', icon: 'ri-file-text-line', phaseIndex: 0,
    steps: [
      {
        id: 'legal-jurisdictions', label: 'Jurisdiction — Physical Locations',
        path: '/legal-register', actionLabel: 'Go to Legal Register', icon: 'ri-map-pin-2-line',
        guidance: {
          intro: 'Your physical office and data centre locations determine which national and regional laws apply to your ISMS. Different jurisdictions have different requirements.',
          tips: [
            'For each physical location, identify the country and jurisdiction. E.g. "London, UK — UK GDPR, Computer Misuse Act, Cyber Essentials guidance".',
            'EU, US, APAC locations each add new regulatory requirements — map them explicitly.',
            'Data residency requirements often flow from the physical location of your data centres.',
          ],
          doneWhen: 'Each physical location is linked to its applicable legal jurisdiction in the Legal Register.',
        },
      },
      {
        id: 'legal-employees', label: 'Jurisdiction of Employees',
        path: '/legal-register', actionLabel: 'Go to Legal Register', icon: 'ri-user-location-line',
        guidance: {
          intro: 'Remote employees working in different countries bring additional legal obligations — especially for data protection, employment law, and encryption export controls.',
          tips: [
            'List the countries where employees are physically located when working.',
            'Note: key employment data protection obligations and any restrictions on security tools per country.',
            'GDPR applies where EU/UK employees process data, regardless of where the company is headquartered.',
          ],
          doneWhen: 'Employee jurisdictions are documented and jurisdiction-specific security obligations are noted.',
        },
      },
      {
        id: 'legal-laws', label: 'Define Relevant Laws in Jurisdictions',
        path: '/legal-register', actionLabel: 'Go to Legal Register', icon: 'ri-book-2-line',
        guidance: {
          intro: 'For each jurisdiction, build out the specific laws, regulations, and standards that apply. This becomes your compliance obligations register.',
          tips: [
            'For each law: name, jurisdiction, what it requires, the control it ties to, review date, and accountable owner.',
            'Common laws: UK GDPR, Data Protection Act 2018, NIS Regulations, Computer Misuse Act, Cyber Essentials.',
            'Sector-specific: FCA (financial services), CQC (healthcare), PCI-DSS (payments), NHS DSPT (health sector).',
            'Link each legal requirement to the ISMS controls that address it.',
          ],
          doneWhen: 'All applicable laws per jurisdiction are catalogued with control mappings and owners.',
        },
      },
      {
        id: 'legal-complete', label: 'Legal Register Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'The Legal Register is your authoritative source of compliance obligations. Auditors will review this and test that your controls address each requirement.',
          tips: [
            'Have your legal team or a qualified solicitor review for completeness.',
            'Assign an owner (Legal or Compliance function) to maintain this register going forward.',
          ],
          doneWhen: 'All applicable legislation across your jurisdictions is documented with owners and control linkages.',
        },
      },
      {
        id: 'legal-refine', label: 'Refine Legal Register as required',
        path: '/legal-register', actionLabel: 'Go to Legal Register', icon: 'ri-refresh-line',
        guidance: {
          intro: 'Laws change. New regulations emerge. Court rulings alter interpretation. Keep the Legal Register current as an ongoing responsibility.',
          tips: [
            'Subscribe to regulatory update services (ICO updates, NCSC alerts, sector regulator newsletters).',
            'Review whenever a new law takes effect or a major regulatory change occurs.',
          ],
          doneWhen: 'Legal Register is reviewed annually and updated within 30 days of any relevant legal change.',
        },
      },
    ],
  },

  // ── Threats ──────────────────────────────────────────────────────────────
  {
    id: 'threats', label: 'Threats', icon: 'ri-virus-line', phaseIndex: 1,
    steps: [
      {
        id: 'threats-migrate', label: 'Migrate PESTLES to Threat Register',
        path: '/pestle', actionLabel: 'Go to PESTLES', icon: 'ri-arrow-right-up-line',
        guidance: {
          intro: 'Your PESTLES Security entries are the raw material for your Threat Register. Use the Migrate button on each PESTLES factor to create threats automatically.',
          tips: [
            'Open each PESTLES factor and use "Migrate to Threat Register" to create a threat from it.',
            'Review and edit the auto-generated threat name and description to be specific to your organisation.',
            'Focus on factors rated "high" or "critical" impact — not every PESTLES entry needs to become a threat.',
          ],
          doneWhen: 'All high-impact PESTLES factors have been migrated or reviewed for migration to the Threat Register.',
        },
      },
      {
        id: 'threats-additional', label: 'Add non-PESTLES threats',
        path: '/threats', actionLabel: 'Go to Threats', icon: 'ri-add-circle-line',
        guidance: {
          intro: 'Operational threats that come from within your organisation or from specific technical vulnerabilities need to be added separately from your PESTLES-derived threats.',
          tips: [
            'Think about: insider threats, configuration errors, third-party supplier compromise, software vulnerabilities specific to your stack, phishing.',
            'Reference NCSC guidance, MITRE ATT&CK, or your sector ISAC for relevant threats you may have missed.',
            'Consider each asset you catalogued — what specific threats target that asset type?',
          ],
          doneWhen: 'Threat Register covers both external context threats and operational/technical threats relevant to your assets.',
        },
      },
      {
        id: 'threats-complete', label: 'Threats Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'A complete Threat Register enables your risk assessment. Every risk you create will reference a threat — the more complete this list, the better your risk coverage.',
          tips: [
            'Aim for 20–40 threats for a medium-sized organisation.',
            'Every threat should have: name, description, priority/impact, and status.',
          ],
          doneWhen: 'Threat Register covers all key threat categories and has been reviewed by the ISMS Manager.',
        },
      },
      {
        id: 'threats-refine', label: 'Refine Threats as required',
        path: '/threats', actionLabel: 'Go to Threats', icon: 'ri-refresh-line',
        guidance: {
          intro: 'The threat landscape evolves. New attack techniques and changes in the threat actor landscape require regular updates to your Threat Register.',
          tips: [
            'Review threats annually as part of the management review.',
            'After any security incident, check whether a new threat category needs to be added.',
            'Subscribe to NCSC threat intelligence and your sector ISAC for ongoing updates.',
          ],
          doneWhen: 'Threat Register is reviewed at least annually and updated after significant security events.',
        },
      },
    ],
  },

  // ── Risk Register ─────────────────────────────────────────────────────────
  {
    id: 'risks', label: 'Risk Register', icon: 'ri-alarm-warning-line', phaseIndex: 1,
    steps: [
      {
        id: 'risk-create', label: 'Create Risks based on PESTLES threats',
        path: '/risks', actionLabel: 'Go to Risks', icon: 'ri-alert-line',
        guidance: {
          intro: 'Each threat can materialise as one or more risks to specific assets. This step converts abstract threats into concrete, assessable risks.',
          tips: [
            'A risk = threat + asset + consequence. E.g. "Ransomware encrypts customer database, causing unavailability and data loss".',
            'Create at least one risk per major threat. High-priority threats may spawn multiple risks against different assets.',
            'Use the "New Risk" button on each PESTLES factor to quickly create and pre-link risks from context.',
          ],
          doneWhen: 'At least one risk has been created for each high-priority threat in the Threat Register.',
        },
      },
      {
        id: 'risk-link-threats', label: 'Link Threats to Risks or vice versa',
        path: '/risks', actionLabel: 'Go to Risks', icon: 'ri-links-line',
        guidance: {
          intro: 'Linking risks to threats creates a traceable chain from context → threat → risk → control. Auditors rely on this traceability.',
          tips: [
            'In each risk record, use Linked Artefacts to link to the relevant threat(s).',
            'A risk can link to multiple threats (e.g. both "ransomware" and "phishing" contribute to the same data encryption risk).',
            'From the threat side, link to all risks that the threat could generate.',
          ],
          doneWhen: 'All risks are linked to at least one threat, and all major threats have at least one linked risk.',
        },
      },
      {
        id: 'risk-raci', label: 'Assign RACI Roles',
        path: '/risks', actionLabel: 'Go to Risks', icon: 'ri-user-shared-line',
        guidance: {
          intro: 'Every risk needs a named owner (Accountable) and someone who manages it day-to-day (Responsible). Without this, risks are unowned and fall through the cracks.',
          tips: [
            'Accountable = senior decision-maker who accepts or mitigates the risk. Usually a department head or C-level.',
            'Responsible = person who does the day-to-day management. Usually IT lead or ISMS Manager.',
            'Distribute ownership based on whose system or process the risk relates to.',
          ],
          doneWhen: 'Every risk has an Accountable and Responsible person assigned.',
        },
      },
      {
        id: 'risk-inherent', label: 'Judge Impact & Likelihood for Inherent Risk',
        path: '/risks', actionLabel: 'Go to Risks', icon: 'ri-scales-3-line',
        guidance: {
          intro: 'Inherent risk is the risk before any controls are applied — the "worst case" baseline if no controls existed.',
          tips: [
            'Impact: How severe would the consequence be? Consider financial, reputational, legal, and operational impact. Score 1–5.',
            'Likelihood: How probable is this risk materialising over the next year without controls? Use threat intelligence.',
            'Inherent scores tend to be high — this is expected and normal.',
          ],
          doneWhen: 'All risks have inherent impact and likelihood scores producing an inherent risk rating.',
        },
      },
      {
        id: 'risk-inherent-complete', label: 'Inherent Risk Assessment Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'Inherent risk assessment gives you the uncontrolled risk landscape. Use this to prioritise which controls matter most.',
          tips: [
            'Your risk matrix should show a heatmap — highest inherent risks need the strongest controls.',
            'Present inherent risk results to senior management before moving to controls.',
          ],
          doneWhen: 'All risks have inherent scores. A risk heatmap has been produced and reviewed by senior management.',
        },
      },
      {
        id: 'risk-link-controls', label: 'Link Risks to Controls',
        path: '/risks', actionLabel: 'Go to Risks', icon: 'ri-shield-keyhole-line',
        guidance: {
          intro: 'For each risk, link the controls that mitigate it. This creates the audit trail: risk → control → evidence.',
          tips: [
            'In each risk record, use Linked Artefacts to link the relevant controls.',
            'A risk may be mitigated by multiple controls (e.g. "Ransomware" → endpoint detection + backup + patch management).',
            'If a risk has no linked controls, document a risk acceptance decision — every gap must be acknowledged.',
          ],
          doneWhen: 'All high and critical risks have at least one linked control. Unmitigated risks have a documented acceptance decision.',
        },
      },
      {
        id: 'risk-residual', label: 'Judge Impact & Likelihood for Residual Risk',
        path: '/risks', actionLabel: 'Go to Risks', icon: 'ri-bar-chart-grouped-line',
        guidance: {
          intro: 'Residual risk is what remains after controls are applied. This is what your organisation actually lives with and must be within the risk appetite approved by senior management.',
          tips: [
            'Residual scores should be lower than inherent scores — if the same, your controls are not effective.',
            'Ask: "Given the controls in place, how likely is this to happen and how bad would it be?"',
            'Residual risks exceeding your risk appetite must be escalated for a risk treatment decision.',
          ],
          doneWhen: 'All risks have residual scores. Over-appetite residual risks are escalated with treatment decisions.',
        },
      },
      {
        id: 'risk-residual-complete', label: 'Residual Risk Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'The residual risk assessment shows the effectiveness of your control framework. This is a core output reviewed by senior management and auditors.',
          tips: [
            'Compare inherent vs residual scores — the delta shows the value your controls provide.',
            'Get formal sign-off from senior management on the residual risk register.',
          ],
          doneWhen: 'All risks have residual scores. Senior management has formally accepted or escalated residual risks.',
        },
      },
      {
        id: 'risk-review', label: 'Review risks upon Major Change',
        path: '/risks', actionLabel: 'Go to Risks', icon: 'ri-refresh-line',
        guidance: {
          intro: 'Risks change when your environment changes. New systems, processes, mergers, and major incidents all trigger a risk review.',
          tips: [
            'Define what constitutes a "major change" that requires a risk review in your risk management procedure.',
            'Common triggers: new IT system deployment, new business process, significant organisational change, major security incident.',
            'Annual comprehensive risk review is the minimum — change-triggered reviews are equally important.',
          ],
          doneWhen: 'A change management process exists that triggers risk reviews. Annual review is scheduled.',
        },
      },
    ],
  },

  // ── Control Matrices ──────────────────────────────────────────────────────
  {
    id: 'controls', label: 'Control Matrices', icon: 'ri-grid-line', phaseIndex: 2,
    steps: [
      {
        id: 'controls-frameworks', label: 'Which Frameworks do you want to align to?',
        path: '/control-matrices', actionLabel: 'Go to Controls', icon: 'ri-layout-grid-line',
        guidance: {
          intro: 'ISO 27001:2022 Annex A has 93 controls across 4 themes. You may also want to align with other frameworks. Choosing upfront determines which controls you will assess.',
          tips: [
            'At minimum, select ISO 27001:2022 Annex A — required for certification.',
            'Add sector-specific frameworks as needed: Cyber Essentials (UK gov contracts), NIST CSF (US/global), SOC 2 (SaaS/cloud), PCI-DSS (payments).',
            'Start with just ISO 27001 unless there is a clear business requirement to add others.',
          ],
          doneWhen: 'Your target framework(s) are selected and the control list is populated in the Control Matrix.',
        },
      },
      {
        id: 'controls-gap', label: 'Gap Analysis of Controls',
        path: '/control-matrices', actionLabel: 'Go to Controls', icon: 'ri-search-eye-line',
        guidance: {
          intro: 'A gap analysis compares where you are now against what ISO 27001 requires. For each control, assess whether it exists, is partially implemented, or is absent.',
          tips: [
            'For each control ask: "Do we have this? Is it documented? Is it effective?" Answer honestly.',
            'Use a simple status: Not implemented / Partially implemented / Fully implemented.',
            'Involve the system/process owners for each control, not just the ISMS Manager.',
            'An honest gap analysis is worth more than an optimistic one — gaps found now can be remediated.',
          ],
          doneWhen: 'Every control has a gap analysis status. Gaps above threshold are flagged for remediation.',
        },
      },
      {
        id: 'controls-scope', label: 'Assign In or Out of Scope label to all Controls',
        path: '/control-matrices', actionLabel: 'Go to Controls', icon: 'ri-focus-3-line',
        guidance: {
          intro: 'Not every ISO 27001 control applies to every organisation. Controls that are genuinely not applicable can be excluded — but you must justify every exclusion in the Statement of Applicability.',
          tips: [
            'Mark each control as: In Scope, Out of Scope (with written justification), or Not Applicable.',
            'Be conservative — auditors will challenge "Not Applicable" decisions. Only exclude where there is unambiguous reason.',
            'Every exclusion must be documented in the Statement of Applicability.',
          ],
          doneWhen: 'Every control has a scope status with written justification for exclusions.',
        },
      },
      {
        id: 'controls-accountable', label: 'Set Accountable People for all Controls',
        path: '/control-matrices', actionLabel: 'Go to Controls', icon: 'ri-user-follow-line',
        guidance: {
          intro: 'Every in-scope control needs an Accountable owner — the senior person ultimately responsible for ensuring the control is effective.',
          tips: [
            'Accountable owners should be at department head level or above for their control domain.',
            'Common assignments: IT controls → CTO; HR controls → HR Director; Physical → Facilities Manager.',
            'Use the Bulk RACI assignment feature to assign accountable owners across all controls in a category at once.',
          ],
          doneWhen: 'All in-scope controls have an Accountable person assigned.',
        },
      },
      {
        id: 'controls-responsible', label: 'Set Responsible People for all Controls',
        path: '/control-matrices', actionLabel: 'Go to Controls', icon: 'ri-user-3-line',
        guidance: {
          intro: 'The Responsible person implements and maintains the control day-to-day. This is usually a more operational role than the Accountable owner.',
          tips: [
            'For technical controls, Responsible is typically the IT team lead or systems administrator.',
            'For policy controls, Responsible is whoever updates and enforces the policy.',
            'Every Responsible person must be aware of their controls — send notifications or hold a formal handover.',
          ],
          doneWhen: 'All in-scope controls have a Responsible person assigned who is aware of their obligations.',
        },
      },
      {
        id: 'controls-initial-complete', label: 'Initial Control Register Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'Your initial control register is populated with scope, gap analysis, and RACI. This is the foundation of your Gap Analysis & Remediation phase.',
          tips: [
            'Review with the ISMS Manager and get senior management sign-off on gap analysis findings.',
            'Produce an initial Statement of Applicability from this register.',
          ],
          doneWhen: 'All in-scope controls have status, RACI, and scope. Senior management has reviewed the gap analysis.',
        },
      },
      {
        id: 'controls-refine', label: 'Refine and Tailor Control Definition and Details',
        path: '/control-matrices', actionLabel: 'Go to Controls', icon: 'ri-tools-line',
        guidance: {
          intro: 'Generic control descriptions need to be tailored to describe exactly how your organisation implements each control — specific to your technology, processes, and structure.',
          tips: [
            'Update each control description to reflect your actual implementation. E.g. "Patch management is performed weekly using Microsoft Intune, covering all 45 Windows endpoints".',
            'Link each control to the policy document that mandates it.',
            'Add measurable control objectives — if you cannot measure it, you cannot evidence it.',
          ],
          doneWhen: 'Control descriptions are tailored to your specific implementation and linked to policy documents.',
        },
      },
      {
        id: 'controls-framework-complete', label: 'Control Framework Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'Your control framework is complete when every in-scope control has a defined implementation, clear ownership, and measurable objective.',
          tips: [
            'Produce the final Statement of Applicability and get it formally approved by senior management.',
            'The SoA is required documentation for ISO 27001 certification — this is a major milestone.',
          ],
          doneWhen: 'Statement of Applicability is produced and approved. All in-scope controls have full definitions and ownership.',
        },
      },
    ],
  },

  // ── Evidence Register ─────────────────────────────────────────────────────
  {
    id: 'evidence', label: 'Evidence Register', icon: 'ri-file-search-line', phaseIndex: 2,
    steps: [
      {
        id: 'evidence-type', label: 'Premade Evidence / Self-made Evidence',
        path: '/evidence', actionLabel: 'Go to Evidence', icon: 'ri-file-add-line',
        guidance: {
          intro: 'Evidence items prove your controls are operating effectively. You can use Mercell Nimbus premade evidence templates or create your own based on existing internal processes.',
          tips: [
            'Premade evidence templates provide a quick starting point for common ISO 27001 control areas.',
            'Self-made evidence is appropriate when you have existing internal processes (change management logs, meeting minutes) that serve as evidence.',
            'Start with premade where available — customise afterwards.',
          ],
          doneWhen: 'You have decided on your evidence approach and started populating evidence items for at least one control.',
        },
      },
      {
        id: 'evidence-generate', label: 'Click Add Evidence Action to generate default evidence',
        path: '/evidence', actionLabel: 'Go to Evidence', icon: 'ri-magic-line',
        guidance: {
          intro: 'Using the Add Evidence Action in Control Matrices automatically generates default evidence items for each control, saving significant time in building your initial evidence register.',
          tips: [
            'Go to each control in Control Matrices and use the Add Evidence Action to generate defaults.',
            'Generated evidence items are starting points — review and tailor them.',
            'Delete evidence items that genuinely do not apply to your operating model.',
          ],
          doneWhen: 'Default evidence items have been generated for all in-scope controls.',
        },
      },
      {
        id: 'evidence-configure', label: 'Configure Evidence Items from Control Register Contents',
        path: '/evidence', actionLabel: 'Go to Evidence', icon: 'ri-settings-3-line',
        guidance: {
          intro: 'Generated evidence items are shells — they need to be configured with meaningful titles, collection frequencies, and evidence types before they are useful. Work through each item and set the core fields.',
          tips: [
            'Rename each evidence item to clearly describe what the evidence actually is (e.g. "Monthly Access Review Report" rather than the default control title).',
            'Set a Collection Frequency for each item — how often this evidence needs to be gathered (monthly, quarterly, annually).',
            'Set the Test Frequency to match the Collection Frequency unless there is a specific audit reason to test more often.',
            'Choose an Evidence Type: Document, Screenshot, Report, Certificate, Log, or Other — this helps filter and audit the register later.',
            'Check that each item is linked to the correct parent control. Generated items inherit their control link, but review if any need correcting.',
            'For any evidence items where collection has already started, set the Last Reviewed Date to record when you last verified the evidence.',
          ],
          doneWhen: 'Every evidence item has a descriptive title, a collection frequency, an evidence type, and confirmed links to its parent control. Items with existing evidence have a last-reviewed date set.',
        },
      },
      {
        id: 'evidence-raci', label: 'Bulk Assign Control RACI to Evidence RACI',
        path: '/evidence', actionLabel: 'Go to Evidence', icon: 'ri-user-shared-2-line',
        guidance: {
          intro: 'Evidence items inherit their ownership from parent controls. Use bulk RACI assignment to propagate control owners to evidence items quickly.',
          tips: [
            'Use the Bulk Assign feature in Evidence Registers to copy RACI from controls to evidence.',
            'Review bulk assignments — some evidence may need different owners than the parent control.',
            'Responsible for evidence = the person who collects and updates evidence regularly.',
          ],
          doneWhen: 'All evidence items have Accountable and Responsible persons assigned.',
        },
      },
      {
        id: 'evidence-current-maturity', label: 'Assess Current Maturity for all Controls with Accountable & Responsible',
        path: '/evidence', actionLabel: 'Go to Evidence', icon: 'ri-bar-chart-line',
        guidance: {
          intro: 'Current maturity asks: how well is each control actually operating today? This baseline shows where you are before any improvement work.',
          tips: [
            'Use a 1–5 scale: 1=Absent, 2=Ad-hoc, 3=Defined, 4=Managed, 5=Optimised.',
            'Be honest — an inflated self-assessment will be challenged by auditors.',
            'Work with the Accountable and Responsible persons for each control to agree the rating.',
          ],
          doneWhen: 'All in-scope controls have a current maturity rating assessed by their owners.',
        },
      },
      {
        id: 'evidence-target-maturity', label: 'Assess Target Maturity for all Controls with Accountable & Responsible Parties',
        path: '/evidence', actionLabel: 'Go to Evidence', icon: 'ri-focus-2-line',
        guidance: {
          intro: 'Target maturity is where each control needs to be to meet your compliance obligations and risk appetite. It does not need to be 5/5 for every control.',
          tips: [
            'Level 3 (Defined) for all controls is often sufficient for initial ISO 27001 certification.',
            'For high-risk control areas, target Level 4 (Managed) or Level 5 (Optimised).',
            'The gap between current and target maturity directly drives your Remediation Register.',
          ],
          doneWhen: 'All in-scope controls have a target maturity rating. Remediation plan is based on the current-to-target gap.',
        },
      },
      {
        id: 'evidence-initial-complete', label: 'Initial Evidence List Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'Your initial evidence register is in place. You know what evidence you need, who owns it, and the current vs target maturity gap.',
          tips: [
            'Share the evidence register with control owners so they know what they need to provide.',
            'Set target dates for evidence collection and build this into your project plan.',
          ],
          doneWhen: 'Evidence register is populated, ownership assigned, and maturity baselines set for all controls.',
        },
      },
      {
        id: 'evidence-collect', label: 'Collect Evidence for Assurance of control operation',
        path: '/evidence', actionLabel: 'Go to Evidence', icon: 'ri-inbox-archive-line',
        guidance: {
          intro: 'Evidence collection is the ongoing process of gathering and attaching documentation that proves your controls operate effectively.',
          tips: [
            'Types of evidence: screenshots, policy documents, meeting minutes, audit logs, test results, training records.',
            'Evidence should be dated and show regular operation — a one-off screenshot is weak; months of logs is strong.',
            'Tag evidence to the specific control(s) it supports.',
            'For automated controls, configure log exports or integrate with your security tooling.',
          ],
          doneWhen: 'Evidence is collected for all critical controls, covering at least the last 3 months of operation.',
        },
      },
      {
        id: 'evidence-refine', label: 'Refine and Tailor Evidence List',
        path: '/evidence', actionLabel: 'Go to Evidence', icon: 'ri-refresh-line',
        guidance: {
          intro: 'As your ISMS matures, the evidence you collect evolves. Some becomes automated; manual evidence is streamlined; new controls require new evidence.',
          tips: [
            'After each internal audit, review which evidence was weak or missing and strengthen those areas.',
            'Work towards automating evidence collection — scheduled reports, integrations with security tools.',
          ],
          doneWhen: 'Evidence list is reviewed and refined at least annually, with ongoing improvement to automated collection.',
        },
      },
    ],
  },

  // ── Remediations ──────────────────────────────────────────────────────────
  {
    id: 'remediations', label: 'Remediations', icon: 'ri-tools-line', phaseIndex: 2,
    steps: [
      {
        id: 'remediations-raise', label: 'Where Control Conformance = Risk, Raise Remediation',
        path: '/remediations', actionLabel: 'Go to Remediations', icon: 'ri-error-warning-line',
        guidance: {
          intro: 'Where your gap analysis shows a control is below target maturity — and that gap creates a risk — raise a formal Remediation to track the work needed to close it.',
          tips: [
            'From the control matrix, use the "Raise Remediation" button on any control where conformance is rated as a risk.',
            'Frame each remediation as a specific, actionable improvement: "Implement MFA for all admin accounts" not "Improve access controls".',
            'Group related remediations where a single project closes multiple gaps.',
          ],
          doneWhen: 'A Remediation record exists for every control gap identified in the gap analysis.',
        },
      },
      {
        id: 'remediations-raci', label: 'Define Remediation RACI',
        path: '/remediations', actionLabel: 'Go to Remediations', icon: 'ri-user-shared-line',
        guidance: {
          intro: 'Remediations need clear ownership to get done. Without a named Accountable and Responsible person, they will not be actioned.',
          tips: [
            'Accountable = person held responsible if the remediation is not completed on time. Usually the control owner.',
            'Responsible = person who does the actual work — an IT team member, project manager, or vendor.',
            'If using an external vendor, the Accountable must still be an internal person.',
          ],
          doneWhen: 'Every remediation has an Accountable and Responsible person assigned.',
        },
      },
      {
        id: 'remediations-requirements', label: 'Define Remediation Requirements',
        path: '/remediations', actionLabel: 'Go to Remediations', icon: 'ri-file-list-3-line',
        guidance: {
          intro: 'A remediation without clear requirements will not be completed correctly. Define the specific deliverable, acceptance criteria, and target date.',
          tips: [
            'For each remediation: what needs to be done, how success will be measured, target date, budget/resource.',
            'Link each remediation to the control(s) it addresses so that closing it auto-updates control maturity.',
            'Prioritise remediations by risk level — close the highest-risk gaps first.',
          ],
          doneWhen: 'All remediations have defined requirements, acceptance criteria, and target completion dates.',
        },
      },
      {
        id: 'remediations-track', label: 'Track Remediation Completion',
        path: '/remediations', actionLabel: 'Go to Remediations', icon: 'ri-progress-4-line',
        guidance: {
          intro: 'Actively tracking remediation progress is essential — remediations are the improvement engine of your ISMS. Review status in every steering committee meeting.',
          tips: [
            'Update status regularly: Open → In Progress → Pending Verification → Closed.',
            'Escalate overdue remediations to the Accountable person and document reasons for delay.',
            'When a remediation closes, verify control maturity has improved and update the evidence register.',
          ],
          doneWhen: 'All remediations are tracked with current status. Overdue items are escalated and documented.',
        },
      },
      {
        id: 'remediations-complete', label: 'Remediations List Complete!',
        isMilestone: true, milestoneColour: 'primary', icon: 'ri-checkbox-circle-fill',
        guidance: {
          intro: 'Your Remediation Register covers all identified control gaps. This does not mean all remediations are closed — it means all gaps are owned and planned.',
          tips: [
            'Present the Remediation Register to senior management as part of the management review.',
            'Agree a schedule for closing critical remediations before your certification audit.',
          ],
          doneWhen: 'All control gaps have a corresponding remediation with a completion plan agreed with senior management.',
        },
      },
    ],
  },

  // ── Issues ────────────────────────────────────────────────────────────────
  {
    id: 'issues', label: 'Issues', icon: 'ri-bug-line', phaseIndex: 3,
    steps: [
      {
        id: 'issues-process', label: 'Identified problems that must be fixed',
        path: '/issues', actionLabel: 'Go to Issues', icon: 'ri-error-warning-line',
        guidance: {
          intro: 'Issues are identified problems that must be fixed — situations where something is not working as it should. ISO 27001 clause 10.1 requires you to identify, correct, and track nonconformities.',
          tips: [
            'Log every identified security problem, policy violation, or process failure as an Issue, even if minor.',
            'Each Issue needs: description, severity, date identified, owner, and target resolution date.',
            'Issues from internal audits, management reviews, and security incidents all belong here.',
            'Issues are reactive records — use Controls and Remediations for proactive gap closure.',
          ],
          doneWhen: 'A process exists to log, track, and resolve security issues. Active issues have owners and target dates.',
        },
      },
    ],
  },

  // ── Incidents ─────────────────────────────────────────────────────────────
  {
    id: 'incidents', label: 'Incidents', icon: 'ri-alarm-warning-fill', phaseIndex: 3,
    steps: [
      {
        id: 'incidents-process', label: 'Security event that has already occurred',
        path: '/incidents', actionLabel: 'Go to Incidents', icon: 'ri-shield-cross-line',
        guidance: {
          intro: 'Security incidents are events that have already occurred — a data breach, malware infection, phishing attack. ISO 27001 Annex A.5.24–5.28 requires a formal incident management process.',
          tips: [
            'Log every security event, even if impact was minimal. The log itself is evidence your process is operating.',
            'Each incident needs: date/time, description, systems/data affected, severity, responder, actions taken, lessons learned.',
            'For notifiable breaches under GDPR, you have 72 hours to report to the ICO for serious breaches.',
            'Post-incident reviews should feed back into your risk register — incidents often reveal previously unidentified gaps.',
          ],
          doneWhen: 'An incident management process is documented and operational. All security events are logged and at least one post-incident review has been completed.',
        },
      },
    ],
  },

];

// ─── Derived helpers ──────────────────────────────────────────────────────────

export const ALL_FLOW_STEPS: FlowStep[] = FLOW_COLUMNS.flatMap(col => col.steps);

// ─── Storage helpers ──────────────────────────────────────────────────────────

const tenantId = (): string => {
  try { return localStorage.getItem('tenantId') || 'default'; } catch { return 'default'; }
};

const CHECKS_KEY     = (): string => `nimbus:ismsflow:checks:${tenantId()}`;
const STEP_CHECKS_KEY = (): string => `nimbus:ismsflow:stepchecks:${tenantId()}`;

export function loadFlowChecks(): Set<string> {
  try {
    const raw = localStorage.getItem(CHECKS_KEY());
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch { return new Set(); }
}

export function saveFlowChecks(s: Set<string>): void {
  try { localStorage.setItem(CHECKS_KEY(), JSON.stringify([...s])); } catch { /* ignore */ }
}

export function loadFlowStepChecks(): Record<string, number[]> {
  try {
    const raw = localStorage.getItem(STEP_CHECKS_KEY());
    return raw ? (JSON.parse(raw) as Record<string, number[]>) : {};
  } catch { return {}; }
}

export function saveFlowStepChecks(r: Record<string, number[]>): void {
  try { localStorage.setItem(STEP_CHECKS_KEY(), JSON.stringify(r)); } catch { /* ignore */ }
}

// ─── Layout constants ──────────────────────────────────────────────────────────

export const COL_WIDTH = 188; // px — individual column width
export const COL_GAP   = 10;  // px — gap between columns
